
















































































































import { Component, Prop, Vue } from "vue-property-decorator";
import * as XLSX from "xlsx";
import { firebase } from "@/config/firebase";
import { Cliente } from "@/typings/store/plugins/easyFirestore/clientes";
import { Local } from "@/typings/store/plugins/easyFirestore/locales";
import { Contrato } from "@/typings/store/plugins/easyFirestore/contratos";
import {
  FilaPlan,
  documentosImportacion,
  leerExcelFacturacion,
  planificarImportacion,
  razonesSocialesDelMenu,
} from "@/helpers/importarExcelFacturacion";
import { formatearNumero } from "@/helpers/facturacion";

const RAIZ = "dev/v1";
const MAX_LOTE = 400;
const COLUMNAS_MAX = 40;

@Component
export default class ImportarExcelFacturacion extends Vue {
  @Prop({ default: () => [] }) readonly clientes!: Cliente[];
  @Prop({ default: () => [] }) readonly locales!: Local[];
  @Prop({ default: () => [] }) readonly contratos!: Contrato[];

  paso = 1;
  archivo: File | null = null;
  leyendo = false;
  guardando = false;
  error = "";
  verOmitidas = false;
  filas: FilaPlan[] = [];
  seleccionadas: FilaPlan[] = [];
  omitidas: Array<{ hoja: string; motivo: string }> = [];
  resultado: Record<string, number | string> | null = null;
  opciones = {
    desde: `${new Date().getFullYear()}-01`,
    diaUf: 5,
    centroCosto: "004",
    liquidacion: "GENERAL UF DIA 5",
    cargarVentas: true,
  };
  terminoISO = `${new Date().getFullYear() + 1}-12-31`;

  headers = [
    { text: "Hoja", value: "hoja.hoja" },
    { text: "Cliente", value: "cliente", sortable: false },
    { text: "Local", value: "local", sortable: false },
    { text: "m²", value: "hoja.m2" },
    { text: "Condiciones (UF/m²)", value: "tramos", sortable: false },
    { text: "Variable", value: "variable", sortable: false },
    { text: "Estado", value: "estado", sortable: false },
  ];

  get nuevosClientes(): number {
    return new Set(this.seleccionadas.filter((f) => f.cliente.nuevo).map((f) => f.cliente.id)).size;
  }

  get nuevosLocales(): number {
    return new Set(this.seleccionadas.filter((f) => f.local.nuevo).map((f) => f.local.id)).size;
  }

  get db(): firebase.firestore.Firestore {
    return firebase.firestore();
  }

  uf(v: number): string {
    return v ? formatearNumero(v, 4) : "0";
  }

  pct(v: number): string {
    return `${formatearNumero(v * 100, 2)}%`;
  }

  seleccionable(f: FilaPlan): boolean {
    return f.contratoExistente === null && f.hoja.tramos.length > 0;
  }

  claseFila(f: FilaPlan): string {
    return this.seleccionable(f) ? "" : "grey--text";
  }

  async leer(): Promise<void> {
    if (!this.archivo) return;
    this.leyendo = true;
    this.error = "";
    try {
      if (!/^\d{4}-\d{2}$/.test(this.opciones.desde)) throw new Error("Indica desde qué mes leer.");
      const libro = XLSX.read(await this.archivo.arrayBuffer(), { type: "array", cellDates: true });
      const hojas: Record<string, unknown[][]> = {};
      for (const nombre of libro.SheetNames) {
        const hoja = libro.Sheets[nombre];
        if (!hoja["!ref"]) continue;
        // Algunas hojas declaran rangos de miles de columnas vacías; los datos usan las primeras.
        const rango = XLSX.utils.decode_range(hoja["!ref"]);
        rango.e.c = Math.min(rango.e.c, COLUMNAS_MAX);
        hojas[nombre] = XLSX.utils.sheet_to_json(hoja, { header: 1, defval: "", range: rango }) as unknown[][];
      }
      const lectura = leerExcelFacturacion(hojas, { desde: this.opciones.desde });
      if (!lectura.hojas.length) throw new Error("No encontré hojas de operador con tabla mensual en ese rango.");
      const menu = Object.keys(hojas).find((n) => n.trim().toUpperCase() === "MENU");
      this.omitidas = lectura.omitidas;
      this.filas = planificarImportacion(lectura.hojas, {
        clientes: this.clientes as unknown as Array<{ id?: string; rut: string; nombre: string }>,
        locales: this.locales as unknown as Array<{ id?: string; codigo: string }>,
        contratos: this.contratos.map((c) => ({ id: c.id, codigo: Number(c.codigo), localId: c.localId, estadoContrato: c.estadoContrato })),
        razonesSociales: menu ? razonesSocialesDelMenu(hojas[menu]) : new Map(),
        generarId: () => this.db.collection(`${RAIZ}/clientes`).doc().id,
      });
      this.seleccionadas = this.filas.filter((f) => this.seleccionable(f));
      this.paso = 2;
    } catch (e) {
      this.error = e instanceof Error ? e.message : "No se pudo leer el archivo.";
    } finally {
      this.leyendo = false;
    }
  }

  async importar(): Promise<void> {
    const [y, m, d] = this.terminoISO.split("-");
    const docs = documentosImportacion(this.seleccionadas, {
      ...this.opciones,
      fechaTermino: `${d}-${m}-${y}`,
    });
    this.paso = 3;
    this.guardando = true;
    this.resultado = null;
    try {
      const escrituras: Array<[string, string, Record<string, unknown>]> = [];
      const agregar = (coleccion: string, lista: Array<Record<string, unknown>>, conId = true) =>
        lista.forEach((doc) => {
          const id = (conId && (doc.id as string)) || this.db.collection(`${RAIZ}/${coleccion}`).doc().id;
          escrituras.push([coleccion, id, { ...doc, id }]);
        });
      agregar("clientes", docs.clientes);
      agregar("locales", docs.locales);
      agregar(
        "contratos",
        docs.contratos.map((c) => ({
          ...c,
          fechaInicio: firebase.firestore.Timestamp.fromDate(c.fechaInicio as Date),
          fechaFin: firebase.firestore.Timestamp.fromDate(c.fechaFin as Date),
        })),
        false
      );
      agregar("condicionesComerciales", docs.condiciones, false);
      agregar("condicionesDivisiones", docs.divisiones, false);
      agregar("ventasLocatarios", docs.ventas);
      for (let i = 0; i < escrituras.length; i += MAX_LOTE) {
        const lote = this.db.batch();
        for (const [col, id, doc] of escrituras.slice(i, i + MAX_LOTE))
          lote.set(this.db.collection(`${RAIZ}/${col}`).doc(id), doc, { merge: true });
        await lote.commit();
      }
      this.resultado = {
        contratos: docs.contratos.length,
        condiciones: docs.condiciones.length,
        divisiones: docs.divisiones.length,
        clientes: docs.clientes.length,
        locales: docs.locales.length,
        ventas: docs.ventas.length,
      };
      this.$emit("importado", this.resultado);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error(e);
      this.resultado = { error: "No se pudo completar la importación. Revisa en Contratos qué quedó guardado antes de volver a intentarlo, para no duplicar contratos." };
    } finally {
      this.guardando = false;
    }
  }
}
